import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useCompany } from '@/contexts/CompanyContext';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';


export interface Position {
  id: string;
  name: string;
  profile_source: 'ideal' | 'standard';
  ideal_profile_id: string | null;
  standard_profile_key: string | null;
  company_id: string | null;
  user_id: string | null;
  order_position: number;
  created_at: string;
  updated_at: string;
}

export interface PositionMember {
  id: string;
  position_id: string;
  submission_id: string;
  created_at: string;
}

export interface PositionWithMembers extends Position {
  members: PositionMember[];
}

export function usePositions() {
  const [positions, setPositions] = useState<PositionWithMembers[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { currentCompany } = useCompany();
  const { user } = useAuth();

  const fetchPositions = useCallback(async () => {
    if (!user) return;
    
    setLoading(true);
    try {
      // Build query filtering by company or user
      let query = supabase
        .from('positions')
        .select('*')
        .is('deleted_at', null)
        .order('order_position', { ascending: true });

      if (currentCompany?.company_id) {
        query = query.eq('company_id', currentCompany.company_id);
      } else {
        query = query.eq('user_id', user.id);
      }

      const { data: positionsData, error: positionsError } = await query;

      if (positionsError) throw positionsError;

      // Fetch all position members
      const positionIds = positionsData?.map(p => p.id) || [];
      let membersData: PositionMember[] = [];
      
      if (positionIds.length > 0) {
        const { data, error: membersError } = await supabase
          .from('position_members')
          .select('*')
          .in('position_id', positionIds);

        if (membersError) throw membersError;
        membersData = data || [];
      }

      // Combine positions with their members
      const positionsWithMembers: PositionWithMembers[] = (positionsData || []).map(position => ({
        ...position,
        profile_source: position.profile_source as 'ideal' | 'standard',
        order_position: position.order_position ?? 0,
        members: membersData.filter(m => m.position_id === position.id)
      }));

      setPositions(positionsWithMembers);
    } catch (error) {
      console.error('Error fetching positions:', error);
      toast.error('Failed to load positions');
    } finally {
      setLoading(false);
    }
  }, [user, currentCompany?.company_id]);

  useEffect(() => {
    fetchPositions();
  }, [fetchPositions]);

  const createPosition = async (name: string) => {
    if (!user) return null;
    
    setSaving(true);
    try {
      // Get max order position to add at end
      const maxOrder = positions.length > 0 
        ? Math.max(...positions.map(p => p.order_position)) + 1 
        : 0;

      const { data, error } = await supabase
        .from('positions')
        .insert({
          name,
          profile_source: 'standard',
          company_id: currentCompany?.company_id || null,
          user_id: user.id,
          order_position: maxOrder
        })
        .select()
        .single();

      if (error) throw error;

      const newPosition: PositionWithMembers = {
        ...data,
        profile_source: data.profile_source as 'ideal' | 'standard',
        order_position: data.order_position ?? maxOrder,
        members: []
      };

      setPositions(prev => [...prev, newPosition]);
      toast.success('Position created');

      return newPosition;
    } catch (error) {
      console.error('Error creating position:', error);
      toast.error('Failed to create position');
      return null;
    } finally {
      setSaving(false);
    }
  };

  const updatePosition = async (id: string, updates: Partial<Omit<Position, 'id' | 'created_at' | 'updated_at'>>) => {
    setSaving(true);
    try {
      const { error } = await supabase
        .from('positions')
        .update(updates)
        .eq('id', id);

      if (error) throw error;

      setPositions(prev => prev.map(p => 
        p.id === id ? { ...p, ...updates } : p
      ));
    } catch (error) {
      console.error('Error updating position:', error);
      toast.error('Failed to update position');
    } finally {
      setSaving(false);
    }
  };

  const deletePosition = async (id: string) => {
    setSaving(true);
    try {
      // Soft delete: set deleted_at instead of removing the row
      const { error } = await supabase
        .from('positions')
        .update({ deleted_at: new Date().toISOString() })
        .eq('id', id);

      if (error) throw error;

      setPositions(prev => prev.filter(p => p.id !== id));
      toast.success('Position deleted');
    } catch (error) {
      console.error('Error deleting position:', error);
      toast.error('Failed to delete position');
    } finally {
      setSaving(false);
    }
  };

  // Optimistic reorder with rollback on error
  const reorderPositions = async (reorderedPositions: PositionWithMembers[]) => {
    const previousPositions = [...positions];
    
    // Optimistically update UI immediately
    setPositions(reorderedPositions);

    try {
      // Batch update order_position for all positions
      const updates = reorderedPositions.map((pos, index) => ({
        id: pos.id,
        order_position: index
      }));

      // Use Promise.all for parallel updates
      const results = await Promise.all(
        updates.map(({ id, order_position }) =>
          supabase
            .from('positions')
            .update({ order_position })
            .eq('id', id)
        )
      );

      // Check for any errors
      const failed = results.find(r => r.error);
      if (failed?.error) throw failed.error;

    } catch (error) {
      console.error('Error reordering positions:', error);
      // Rollback to previous state
      setPositions(previousPositions);
      toast.error('Failed to save order');
    }
  };

  const addMemberToPosition = async (positionId: string, submissionId: string) => {
    setSaving(true);
    try {
      const { data, error } = await supabase
        .from('position_members')
        .insert({
          position_id: positionId,
          submission_id: submissionId
        })
        .select()
        .single();

      if (error) throw error;

      setPositions(prev => prev.map(p => 
        p.id === positionId 
          ? { ...p, members: [...p.members, data] }
          : p
      ));
      toast.success('Member added');
    } catch (error: any) {
      console.error('Error adding member:', error);
      if (error.code === '23505') {
        toast.error('This candidate is already assigned to this position');
      } else {
        toast.error('Failed to add member');
      }
    } finally {
      setSaving(false);
    }
  };

  const removeMemberFromPosition = async (positionId: string, submissionId: string) => {
    setSaving(true);
    try {
      const { error } = await supabase
        .from('position_members')
        .delete()
        .eq('position_id', positionId)
        .eq('submission_id', submissionId);

      if (error) throw error;

      setPositions(prev => prev.map(p => 
        p.id === positionId 
          ? { ...p, members: p.members.filter(m => m.submission_id !== submissionId) }
          : p
      ));
    } catch (error) {
      console.error('Error removing member:', error);
      toast.error('Failed to remove member');
    } finally {
      setSaving(false);
    }
  };

  return {
    positions,
    loading,
    saving,
    createPosition,
    updatePosition,
    deletePosition,
    addMemberToPosition,
    removeMemberFromPosition,
    reorderPositions,
    refetch: fetchPositions
  };
}
